/* eslint-disable react-refresh/only-export-components -- provider + hook pattern requires co-location */
import { createContext, useContext, useEffect } from 'react';

type Theme = 'light';
type ResolvedTheme = 'light';

interface ThemeContextValue {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  resolvedTheme: ResolvedTheme;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const STORAGE_KEY = 'theme';

function persistLightTheme(): void {
  if (typeof window === 'undefined') return;
  try {
    if (window.localStorage.getItem(STORAGE_KEY) !== 'light') {
      window.localStorage.setItem(STORAGE_KEY, 'light');
    }
  } catch {
    // Ignore storage access failures and keep the app in light mode.
  }
}

function applyTheme(): void {
  document.documentElement.classList.remove('dark');
}

export function ThemeProvider({
  children,
  defaultTheme = 'light',
}: {
  children: React.ReactNode;
  defaultTheme?: Theme;
}): React.ReactElement {
  const theme: Theme = defaultTheme;
  const resolved: ResolvedTheme = 'light';

  const setTheme = (newTheme: Theme): void => {
    if (typeof window !== 'undefined') {
      try {
        window.localStorage.setItem(STORAGE_KEY, newTheme);
      } catch {
        // Ignore storage access failures and keep the app in light mode.
      }
    }
    applyTheme();
  };

  useEffect(() => {
    persistLightTheme();
    applyTheme();
  }, []);

  return <ThemeContext.Provider value={{ theme, setTheme, resolvedTheme: resolved }}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
